import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #featuredCarousel = null;

  /** @type {HTMLButtonElement[]} */
  #featuredDots = [];

  /** @type {(() => void) | null} */
  #onFeaturedCarouselScroll = null;

  /** @type {((event: Event) => void) | null} */
  #onFeaturedDotClick = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupFeaturedCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#teardownFeaturedCarousel();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Wire scroll-snap carousel dots for the bottom featured rail.
   */
  #setupFeaturedCarousel() {
    const carousel = this.querySelector('[data-drawer-carousel]');
    if (!(carousel instanceof HTMLElement)) return;

    this.#featuredCarousel = carousel;
    this.#onFeaturedCarouselScroll = () => this.#syncFeaturedCarouselDots();
    carousel.addEventListener('scroll', this.#onFeaturedCarouselScroll, { passive: true });

    const dots = this.querySelector('[data-drawer-carousel-dots]');
    if (!(dots instanceof HTMLElement)) return;

    this.#featuredDots = /** @type {HTMLButtonElement[]} */ ([...dots.querySelectorAll('.menu-drawer__featured-dot')]);
    this.#onFeaturedDotClick = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const button = target.closest('.menu-drawer__featured-dot');
      if (!(button instanceof HTMLButtonElement)) return;
      const index = Number(button.dataset.index);
      if (Number.isNaN(index)) return;
      this.#scrollFeaturedCarouselTo(index);
    };

    dots.addEventListener('click', this.#onFeaturedDotClick);
    this.#syncFeaturedCarouselDots();
  }

  #teardownFeaturedCarousel() {
    if (this.#featuredCarousel && this.#onFeaturedCarouselScroll) {
      this.#featuredCarousel.removeEventListener('scroll', this.#onFeaturedCarouselScroll);
    }
    const dots = this.querySelector('[data-drawer-carousel-dots]');
    if (dots && this.#onFeaturedDotClick) {
      dots.removeEventListener('click', this.#onFeaturedDotClick);
    }
    this.#featuredCarousel = null;
    this.#featuredDots = [];
    this.#onFeaturedCarouselScroll = null;
    this.#onFeaturedDotClick = null;
  }

  /**
   * @param {number} index
   */
  #scrollFeaturedCarouselTo(index) {
    const carousel = this.#featuredCarousel;
    if (!carousel) return;
    const slides = carousel.querySelectorAll('.menu-drawer__featured-content-list-item');
    const slide = slides[index];
    if (!(slide instanceof HTMLElement)) return;
    carousel.scrollTo({
      left: slide.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #syncFeaturedCarouselDots() {
    const carousel = this.#featuredCarousel;
    if (!carousel || !this.#featuredDots.length) return;

    const slides = [...carousel.querySelectorAll('.menu-drawer__featured-content-list-item')];
    if (!slides.length) return;

    const center = carousel.scrollLeft + carousel.clientWidth / 2;
    let activeIndex = 0;
    let closest = Infinity;

    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - center);
      if (distance < closest) {
        closest = distance;
        activeIndex = index;
      }
    });

    this.#featuredDots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
